import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart } from "lucide-react";
import { ApiError, errorMessage } from "@/api/errors";
import { Spinner } from "@/components/ui";
import { useAddToCart } from "@/features/cart/api";
import { cn } from "@/lib/cn";
import { imageUrl } from "@/lib/image";
import { useFlyToCart } from "@/motion/useFlyToCart";
import { toast } from "@/stores/toast";

/**
 * One-tap add from a product card. Listings with options (size, colour…) open the product page
 * instead, because the buyer has to choose one.
 */
export function QuickAdd({ slug, title, cover, className }: { slug: string; title: string; cover?: string | null; className?: string }) {
  const add = useAddToCart();
  const fly = useFlyToCart();
  const navigate = useNavigate();
  const ref = useRef<HTMLButtonElement>(null);
  return (
    <button
      ref={ref}
      type="button"
      aria-label={`Add ${title} to cart`}
      disabled={add.isPending}
      onClick={() =>
        add.mutate(
          { product: slug },
          {
            onSuccess: () => {
              const card = ref.current?.closest("[data-card]")?.querySelector("img") ?? ref.current;
              fly(card, cover ? imageUrl(cover, 240) : undefined);
              toast.success("Added to cart", title);
            },
            onError: (e) => {
              if (e instanceof ApiError && e.code === "VARIANT_REQUIRED") {
                toast.info("Choose an option first", "Pick a size, colour or model on the listing.");
                navigate(`/p/${slug}`);
              } else toast.error("Couldn't add to cart", errorMessage(e));
            },
          },
        )
      }
      className={cn(
        "grid size-10 place-items-center rounded-full border border-[var(--border-glow)] bg-void/75 text-ink shadow-glow-1 backdrop-blur transition",
        "hover:bg-[var(--primary-strong)] hover:text-white focus-visible:opacity-100 group-hover/card:opacity-100 [@media(hover:hover)_and_(min-width:768px)]:opacity-0",
        className,
      )}
    >
      {add.isPending ? <Spinner className="size-4" label="Adding" /> : <ShoppingCart className="size-4" aria-hidden />}
    </button>
  );
}
